import React from 'react';
import { Gift, DollarSign, Clock, Users, Star, CheckCircle, XCircle, Image as ImageIcon } from 'lucide-react';
import { useTogglePackagePopular } from '../../../services/vendorServicePackageService';
import { useToastContext } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';

const PackagesTab = ({ vendorProfile, onUpdate }) => {
  const packages = vendorProfile?.vendor_service_packages || vendorProfile?.vendorServicePackages || [];
  const { success, error: showError } = useToastContext();
  const { user } = useAuth();
  const isAdmin = user?.user_type === 'admin';

  const togglePopularMutation = useTogglePackagePopular({
    onSuccess: () => {
      success('Package popular status updated successfully!');
      onUpdate?.(); // Trigger parent refetch if callback provided
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update package popular status');
    }
  });

  const handleTogglePopular = (pkg) => {
    togglePopularMutation.mutate({ id: pkg.id, isAdmin });
  };

  if (!packages || packages.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12">
        <Gift className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Packages Found</h3>
        <p className="text-sm text-gray-600">This vendor hasn't created any service packages yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
          Service Packages ({packages.length})
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Package Image */}
            <div className="relative h-32 sm:h-40 bg-gray-100">
              {pkg.image_url ? (
                <img
                  src={pkg.image_url}
                  alt={pkg.package_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
                </div>
              )}
              
              {/* Status Badge */}
              <div className="absolute top-2 right-2">
                {pkg.is_active ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Inactive
                  </span>
                )}
              </div>

              {pkg.is_popular && (
                <div className="absolute top-2 left-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    Popular
                  </span>
                </div>
              )}
            </div>

            {/* Package Content */}
            <div className="p-3 sm:p-4">
              <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                {pkg.package_name}
              </h4>

              {pkg.description && (
                <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2">
                  {pkg.description}
                </p>
              )}

              {/* Package Details */}
              <div className="space-y-2 mb-3 sm:mb-4">
                {pkg.base_price !== undefined && pkg.base_price !== null && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                    <span className="font-medium">
                      ${Number(pkg.base_price).toFixed(2)}
                    </span>
                    {pkg.price_type && (
                      <span className="ml-1 text-gray-500">
                        ({pkg.price_type})
                      </span>
                    )}
                  </div>
                )}

                {pkg.duration_hours && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                    <span>{pkg.duration_hours} hours</span>
                  </div>
                )}

                {pkg.max_guests && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                    <span>Up to {pkg.max_guests} guests</span>
                  </div>
                )}

                {pkg.service && (
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <Gift className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                    <span className="truncate">
                      {pkg.service.service_name || 'N/A'}
                    </span>
                  </div>
                )}
              </div>

              {/* Toggle Popular Button */}
              <div className="pt-3 border-t border-gray-200">
                <button
                  onClick={() => handleTogglePopular(pkg)}
                  disabled={togglePopularMutation.isPending}
                  className={`w-full sm:w-auto flex items-center justify-center px-3 py-2 text-xs sm:text-sm font-medium rounded-md border transition-colors ${
                    pkg.is_popular 
                      ? "text-yellow-600 border-yellow-600 hover:bg-yellow-50 bg-yellow-50"
                      : "text-gray-600 border-gray-300 hover:bg-gray-50 bg-white"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={pkg.is_popular ? 'Remove from popular' : 'Mark as popular'}
                >
                  <Star className={`w-3 h-3 sm:w-4 sm:h-4 ${pkg.is_popular ? 'fill-current' : ''} mr-1.5 sm:mr-2`} />
                  <span>
                    {pkg.is_popular ? 'Popular' : 'Mark Popular'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PackagesTab;

